import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const septicModule: ChecklistModuleDefinition = {
  code: 'septic',
  name: 'Septic/Sewer',
  description: 'Septic systems, municipal sewer, and waste management',
  categories: [
    {
      code: 'sewer_availability',
      name: 'Sewer Availability',
      items: [
        {
          code: 'wastewater_type',
          title: 'Wastewater system type',
          description: 'Identify current wastewater handling',
          isGroupTask: false,
          dataFields: [
            { key: 'system_type', label: 'System Type', type: 'select', options: [
              { value: 'municipal', label: 'Municipal Sewer' },
              { value: 'septic', label: 'Septic System' },
              { value: 'cesspool', label: 'Cesspool' },
              { value: 'composting', label: 'Composting Toilet' },
              { value: 'none', label: 'No System' },
            ]},
          ],
        },
        {
          code: 'municipal_sewer',
          title: 'Municipal sewer check',
          description: 'Check for public sewer availability',
          isGroupTask: false,
          dataFields: [
            { key: 'available', label: 'Available', type: 'boolean' },
            { key: 'connected', label: 'Connected', type: 'boolean' },
            { key: 'distance_to_main', label: 'Distance to Main', type: 'number', unit: 'feet' },
            { key: 'connection_cost', label: 'Est. Connection Cost', type: 'number', unit: 'dollars' },
          ],
        },
      ],
    },
    {
      code: 'existing_septic',
      name: 'Existing Septic System',
      items: [
        {
          code: 'septic_records',
          title: 'Septic system records',
          description: 'Obtain as-built and permit records',
          isGroupTask: false,
          dataFields: [
            { key: 'records_obtained', label: 'Records Obtained', type: 'boolean' },
            { key: 'install_date', label: 'Install Date', type: 'date' },
            { key: 'permitted', label: 'Properly Permitted', type: 'boolean' },
            { key: 'system_type', label: 'System Type', type: 'select', options: [
              { value: 'conventional', label: 'Conventional' },
              { value: 'aerobic', label: 'Aerobic' },
              { value: 'mound', label: 'Mound System' },
              { value: 'drip', label: 'Drip System' },
              { value: 'sand_filter', label: 'Sand Filter' },
              { value: 'constructed_wetland', label: 'Constructed Wetland' },
              { value: 'unknown', label: 'Unknown' },
            ]},
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'No permit records found' },
            { flag: 'yellow', condition: 'Old system, records incomplete' },
          ],
        },
        {
          code: 'septic_tank',
          title: 'Septic tank assessment',
          description: 'Evaluate tank size, material, condition',
          isGroupTask: false,
          dataFields: [
            { key: 'tank_size', label: 'Tank Size', type: 'number', unit: 'gallons' },
            { key: 'tank_material', label: 'Tank Material', type: 'select', options: [
              { value: 'concrete', label: 'Concrete' },
              { value: 'fiberglass', label: 'Fiberglass' },
              { value: 'plastic', label: 'Plastic/Poly' },
              { value: 'steel', label: 'Steel' },
              { value: 'unknown', label: 'Unknown' },
            ]},
            { key: 'tank_condition', label: 'Tank Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
              { value: 'unknown', label: 'Unknown' },
            ]},
            { key: 'adequate_for_home', label: 'Adequate for Home Size', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Tank in poor condition' },
            { flag: 'red', condition: 'Tank undersized for home' },
            { flag: 'yellow', condition: 'Tank condition unknown' },
          ],
        },
        {
          code: 'drain_field',
          title: 'Drain field assessment',
          description: 'Evaluate drain field condition and capacity',
          isGroupTask: false,
          dataFields: [
            { key: 'drain_field_type', label: 'Type', type: 'string' },
            { key: 'size_sqft', label: 'Size', type: 'number', unit: 'sq ft' },
            { key: 'condition', label: 'Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
              { value: 'failing', label: 'Failing' },
              { value: 'unknown', label: 'Unknown' },
            ]},
            { key: 'signs_of_failure', label: 'Signs of Failure', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Drain field failing' },
            { flag: 'red', condition: 'Drain field in poor condition' },
            { flag: 'yellow', condition: 'Drain field showing age' },
            { flag: 'green', condition: 'Drain field in good condition' },
          ],
        },
        {
          code: 'septic_pumping',
          title: 'Pumping history',
          description: 'Review maintenance and pumping records',
          isGroupTask: false,
          dataFields: [
            { key: 'last_pumped', label: 'Last Pumped', type: 'date' },
            { key: 'pumping_frequency', label: 'Pumping Frequency', type: 'string' },
            { key: 'service_provider', label: 'Service Provider', type: 'string' },
            { key: 'issues_reported', label: 'Issues Reported', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Overdue for pumping' },
            { flag: 'red', condition: 'History of problems' },
          ],
        },
        {
          code: 'septic_inspection',
          title: 'Septic inspection',
          description: 'Professional septic system inspection',
          isGroupTask: false,
          dataFields: [
            { key: 'inspection_done', label: 'Inspection Completed', type: 'boolean' },
            { key: 'inspection_date', label: 'Inspection Date', type: 'date' },
            { key: 'inspector', label: 'Inspector', type: 'string' },
            { key: 'passed', label: 'Passed Inspection', type: 'boolean' },
            { key: 'issues_found', label: 'Issues Found', type: 'array' },
            { key: 'repair_estimates', label: 'Repair Estimates', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Failed inspection - major issues' },
            { flag: 'red', condition: 'Inspection found issues' },
            { flag: 'green', condition: 'Passed inspection' },
          ],
        },
        {
          code: 'septic_reserve_area',
          title: 'Reserve drain field area',
          description: 'Verify reserve/replacement area exists',
          isGroupTask: false,
          dataFields: [
            { key: 'reserve_exists', label: 'Reserve Area Exists', type: 'boolean' },
            { key: 'reserve_size', label: 'Reserve Size', type: 'number', unit: 'sq ft' },
            { key: 'reserve_usable', label: 'Reserve Usable', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'No reserve area available' },
          ],
        },
      ],
    },
    {
      code: 'new_septic',
      name: 'New Septic Potential',
      items: [
        {
          code: 'perc_test',
          title: 'Percolation test',
          description: 'Soil percolation testing for septic',
          isGroupTask: false,
          dataFields: [
            { key: 'test_done', label: 'Test Completed', type: 'boolean' },
            { key: 'test_date', label: 'Test Date', type: 'date' },
            { key: 'perc_rate', label: 'Perc Rate', type: 'string' },
            { key: 'passed', label: 'Passed', type: 'boolean' },
            { key: 'suitable_areas', label: 'Suitable Areas Identified', type: 'number' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Failed perc test, no alternatives' },
            { flag: 'red', condition: 'Poor perc, engineered system needed' },
            { flag: 'green', condition: 'Good percolation' },
          ],
        },
        {
          code: 'septic_permit',
          title: 'Septic permit feasibility',
          description: 'Check permit requirements and viability',
          isGroupTask: false,
          dataFields: [
            { key: 'permit_available', label: 'Permit Available', type: 'boolean' },
            { key: 'system_type_required', label: 'System Type Required', type: 'string' },
            { key: 'estimated_cost', label: 'Estimated System Cost', type: 'number', unit: 'dollars' },
            { key: 'permit_cost', label: 'Permit Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'septic_restrictions',
          title: 'Septic restrictions',
          description: 'Regulatory limitations on septic systems',
          isGroupTask: false,
          dataFields: [
            { key: 'setback_to_well', label: 'Setback to Well', type: 'number', unit: 'feet' },
            { key: 'setback_to_property_line', label: 'Setback to Property Line', type: 'number', unit: 'feet' },
            { key: 'setback_to_water', label: 'Setback to Water', type: 'number', unit: 'feet' },
            { key: 'other_restrictions', label: 'Other Restrictions', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Restrictive setbacks limit options' },
          ],
        },
      ],
    },
  ],
};

